/**
 * Report and drawing production (CLAUDE.md 8, 9, 13).
 *
 * The A0 exit criterion this completes: "downloads a PDF and a DXF". Until now the
 * pipeline existed only as CLI smoke scripts, and this panel said so in a paragraph.
 *
 * Two responsibility rules are surfaced here rather than left to the server to refuse:
 *
 * - **FOR CONSTRUCTION requires an approved position** (2.2). The option is disabled
 *   with the reason given, instead of letting the engineer pick it and receive a 409.
 * - **A report is rendered from the *saved* position**, not from the form. So an
 *   unsaved edit is called out before the engineer produces a document that does not
 *   match what is on their screen.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, Eye, FileText, LifeBuoy, PenLine } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useCreateDrawing, useCreateReport, useReportPolling, useReports } from '@/api/hooks'
import type { DocStatus, Position } from '@/api/types'
import { ReportPreviewDialog } from '@/components/calc/ReportPreviewDialog'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { useToast } from '@/components/ui/toast'
import { downloadFile } from '@/lib/download'
import { formatBytes, formatRelative } from '@/lib/format'

const DOC_STATUSES: { value: DocStatus; labelKey: string }[] = [
  { value: 'draft', labelKey: 'report.status.draft' },
  { value: 'review', labelKey: 'report.status.review' },
  { value: 'construction', labelKey: 'report.status.construction' },
]

export function ReportPanel({ position, dirty }: { position: Position; dirty: boolean }) {
  const { t, i18n } = useTranslation(['common', 'report'])
  const { toast } = useToast()

  const [docStatus, setDocStatus] = useState<DocStatus>('draft')
  const [lang, setLang] = useState(i18n.language.slice(0, 2))
  const [activeReportId, setActiveReportId] = useState<number | null>(null)
  // The report being read on screen, which is not necessarily the one just generated:
  // a checker usually wants the last issued document, not a new render (14.3).
  const [previewId, setPreviewId] = useState<number | null>(null)

  const { data: reports } = useReports(position.id)
  const createReport = useCreateReport(position.id)
  const createDrawing = useCreateDrawing(position.id)
  const { data: polled } = useReportPolling(activeReportId, activeReportId !== null)

  const approved = position.review_state === 'approved'
  const calculated = position.status !== 'draft'

  const onGenerate = async () => {
    try {
      const report = await createReport.mutateAsync({ lang, doc_status: docStatus })
      setActiveReportId(report.id)
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    }
  }

  const onDrawing = async (format: 'dxf' | 'pdf') => {
    try {
      const drawing = await createDrawing.mutateAsync({ format })
      if (drawing.status === 'done') {
        await downloadFile(`/api/v1/drawings/${drawing.id}/file`)
      } else {
        toast({ title: t('report.generating'), variant: 'info' })
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    }
  }

  const current = polled ?? reports?.find((r) => r.id === activeReportId)

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">{t('nav.report')}</h2>

      {!calculated && (
        <p className="rounded-md bg-[var(--status-warn-bg)] p-3 text-sm text-[var(--status-warn)]">
          {t('errors:error.report.not_calculated')}
        </p>
      )}

      {dirty && (
        // The report renders the saved position, not the form. Saying so beforehand
        // avoids an engineer issuing a document that does not match their screen.
        <p className="rounded-md bg-[var(--status-warn-bg)] p-3 text-sm text-[var(--status-warn)]">
          {t('report.unsaved_warning')}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('report.generate')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <Field>
            <FieldLabel>{t('report.language')}</FieldLabel>
            <select
              value={lang}
              onChange={(event) => setLang(event.target.value)}
              className="h-11 w-full rounded-md border border-border bg-surface px-3 text-text"
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </Field>

          <Field description={!approved ? t('report.construction_needs_approval') : undefined}>
            <FieldLabel>{t('report.doc_status')}</FieldLabel>
            <select
              value={docStatus}
              onChange={(event) => setDocStatus(event.target.value as DocStatus)}
              className="h-11 w-full rounded-md border border-border bg-surface px-3 text-text"
            >
              {DOC_STATUSES.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  // Only an approved position may be issued FOR CONSTRUCTION (2.2).
                  disabled={option.value === 'construction' && !approved}
                >
                  {t(option.labelKey, { ns: 'report' })}
                </option>
              ))}
            </select>
          </Field>

          <Button
            variant="primary"
            onClick={onGenerate}
            loading={createReport.isPending}
            disabled={!calculated}
            className="w-full"
          >
            <FileText size={16} aria-hidden />
            {t('report.generate')}
          </Button>

          {current && (
            <div
              aria-live="polite"
              className="flex items-center justify-between gap-3 rounded-md border border-border p-3 text-sm"
            >
              <span>
                {current.status === 'done'
                  ? `${formatBytes(current.byte_size)}${
                      current.page_count
                        ? ` · ${t('report.pages', { count: current.page_count })}`
                        : ''
                    }`
                  : current.status === 'error'
                    ? t(`errors:${current.error_key ?? 'error.report.render_failed'}`)
                    : t('report.generating')}
              </span>
              {current.status === 'done' && (
                <span className="flex shrink-0 gap-2">
                  {/* Read it here rather than handing the document to whatever the
                      phone opens PDFs with. A checker signing off on a phone is the
                      review-first case section 14.3 asks for. */}
                  <Button size="sm" onClick={() => setPreviewId(current.id)}>
                    <Eye size={15} aria-hidden />
                    {t('action.view')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => void downloadFile(`/api/v1/reports/${current.id}/file`)}
                  >
                    <Download size={15} aria-hidden />
                    {t('action.download')}
                  </Button>
                </span>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('drawing.title')}</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-wrap gap-2">
          <Button onClick={() => onDrawing('dxf')} loading={createDrawing.isPending}>
            <PenLine size={15} aria-hidden />
            DXF
          </Button>
          <Button onClick={() => onDrawing('pdf')} loading={createDrawing.isPending}>
            <PenLine size={15} aria-hidden />
            PDF
          </Button>
        </CardBody>
      </Card>

      {(reports?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('report.title_short')}</CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="space-y-2 text-sm">
              {reports?.map((report) => (
                <li key={report.id} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate">
                    <span className="uppercase">{report.lang}</span>
                    {' · '}
                    {t(`report.status.${report.doc_status}`, { ns: 'report' })}
                    {' · '}
                    <span className="text-text-muted">
                      {formatRelative(report.created_at, i18n.language)}
                    </span>
                  </span>
                  {report.status === 'done' && (
                    <span className="flex shrink-0 items-center">
                      <Button
                        size="iconSm"
                        variant="ghost"
                        aria-label={t('action.view')}
                        onClick={() => setPreviewId(report.id)}
                      >
                        <Eye size={15} aria-hidden />
                      </Button>
                      <Button
                        size="iconSm"
                        variant="ghost"
                        aria-label={t('action.download')}
                        onClick={() => void downloadFile(`/api/v1/reports/${report.id}/file`)}
                      >
                        <Download size={15} aria-hidden />
                      </Button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {/* The snapshot travels with the request, so staff replay exactly these
          inputs rather than a description of them (12.3). */}
      <Link
        to={`/support/new?position=${position.id}`}
        className="inline-flex h-9 items-center gap-2 text-sm text-text-muted hover:text-text"
      >
        <LifeBuoy size={15} aria-hidden />
        {t('support.report_problem')}
      </Link>

      <ReportPreviewDialog
        reportId={previewId}
        byteSize={reports?.find((report) => report.id === previewId)?.byte_size}
        pageCount={reports?.find((report) => report.id === previewId)?.page_count}
        onClose={() => setPreviewId(null)}
      />
    </div>
  )
}
